import {Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Router} from "@angular/router";
import {LoggingService} from "../../../services/logger/logging.service";
import {isDefined, isTrue, notDefined, unsubscribe} from "../../../libs/utils/epic.utils";
import {ButtonComponent} from "../../../libs/button/button.component";
import {IconComponent} from "../../../libs/icon/icon.component";
import {Observable, Subscription} from "rxjs";
import {Permissions} from "../../../services/auth/permissions.item";
import {PermissionService} from "../../../services/auth/permission.service";
import {FormService} from "../../../services/form.service";
import {FormOptions} from "../../../libs/form/items/form-options";
import {FormWrapper} from "../../../libs/form/items/form.wrapper";
import {Validators} from "@angular/forms";
import {Priority} from "../../../libs/enum/priority.enum";
import {FormMetadata} from "../../../libs/form/items/form-metadata";
import {ModalConfig} from "../../../libs/modal/modal.config";
import {FormComponent} from "../../../libs/form/form.component";
import {FormSwitchComponent} from "../../../libs/form/form-switch/form-switch.component";
import {FormTextEditorComponent} from "../../../libs/form/form-text-editor/form-text-editor.component";
import {ModalComponent} from "../../../libs/modal/modal.component";
import {TranslatePipe} from "@ngx-translate/core";
import {FormInputComponent} from "../../../libs/form/form-input/form-input.component";
import {FormSelectComponent} from "../../../libs/form/form-select/form-select.component";
import {I18nService} from "../../../services/i18n/i18n.service";
import {NgbModalOptions} from "@ng-bootstrap/ng-bootstrap";
import {ValidationErrorDto} from "../../../repositories/dto/validation-error.dto";
import {AccValidator} from "../../../libs/utils/validator";
import {FormFileComponent} from "../../../libs/form/form-file/form-file.component";
import {FormFileConfig} from "../../../libs/form/form-file/form-file-config";
import {Size} from "../../../base/data/size";
import {SupportTicketDto} from "../../../repositories/dto/support-ticket.dto";
import {SupportTicketService} from "../../../services/support-ticket.service";
import {FormTextEditorConfig} from "../../../libs/form/form-text-editor/form-text-editor.config";
import {FormStateEnum} from "../../../libs/form/items/form-state.enum";
import {NotificationService} from "../../../services/notification/notification.service";
import {StateService} from "../../../services/state/state.service";
import {OrgDto} from "../../../repositories/dto/org.dto";
import {captureTab, isScreenshotSupported} from "./tab-screenshot";
import {SupportUnavailableComponent} from "../support-unavailable/support-unavailable.component";

@Component({
  selector: 'acc-support-widget',
  imports: [
    ButtonComponent,
    IconComponent,
    FormComponent,
    FormSwitchComponent,
    FormTextEditorComponent,
    ModalComponent,
    TranslatePipe,
    FormInputComponent,
    FormSelectComponent,
    FormFileComponent,
    SupportUnavailableComponent
  ],
  templateUrl: './support-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './support-widget.component.scss'
})
export class SupportWidgetComponent implements OnInit, OnDestroy {

  // core's limits for a ticket (MediasWrapper): 10 MB per file, 11 MB per request
  private static readonly MAX_FILE_BYTES: number = 10 * 1024 * 1024;
  private static readonly MAX_REQUEST_BYTES: number = 11 * 1024 * 1024;

  @ViewChild('supportTicketTpl')
  protected supportTicketTpl: ModalComponent | undefined;

  @ViewChild('supportUnavailableTpl')
  protected supportUnavailableTpl: ModalComponent | undefined;

  @ViewChild('attachment1')
  public attachment1: FormFileComponent | undefined;

  @ViewChild('attachment2')
  public attachment2: FormFileComponent | undefined;

  @ViewChild('attachment3')
  public attachment3: FormFileComponent | undefined;

  protected readonly modalConfig = new ModalConfig({
    inputConfirmMsgCode: 'support.ticket.create',
    autoClose: false,
    inputConfirmLoadingTimeout: 15000
  });
  protected readonly unavailableConfig = new ModalConfig({inputCancelMsgCode: 'modal.close'});
  protected readonly modalOptions: NgbModalOptions = {windowClass: 'acc-screenshot-ignore'};
  protected readonly fileConfig: FormFileConfig = new FormFileConfig({size: Size.SMALL, accept: '*'});
  protected readonly editorConfig: FormTextEditorConfig = new FormTextEditorConfig({keyDelay: 10});
  protected perms: Permissions;
  protected wrapper: FormWrapper | undefined;
  protected showWidget: boolean = true;
  protected showAttachments: boolean = false;
  protected org: OrgDto | undefined = undefined;
  protected screenshot: Blob | undefined;
  protected screenshotUrl: string | undefined;
  protected capturing: boolean = false;

  private readonly log: LoggingService = inject(LoggingService);
  private readonly router: Router = inject(Router);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly formService: FormService = inject(FormService);
  private readonly i18n: I18nService = inject(I18nService);
  private readonly supportTicketService: SupportTicketService = inject(SupportTicketService);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly stateService: StateService = inject(StateService);

  private readonly subs: Subscription[] = [];
  private readonly formSubs: Subscription[] = [];
  private version: string | undefined;

  constructor() {
    this.perms = this.permissionService.getPerms();
    this.supportTicketService.registerSupportWidget(this);
  }

  ngOnInit(): void {
    this.subs.push(
      this.permissionService.accChanged().subscribe((p) => this.perms = p),
      this.stateService.getOrg().subscribe((o) => this.org = o),
      this.stateService.getInfo().subscribe((i) => this.version = i.version)
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
    unsubscribe(this.formSubs);
    this.clearScreenshot();
    this.showWidget = true;
    this.showAttachments = false;
  }

  // Without Zammad nobody should write a request that cannot be sent
  public openSupportTicket(): void {
    this.subs.push(this.supportTicketService.isAvailable().subscribe((available) => {
      if (available) {
        this.buildForm();
        this.supportTicketTpl?.open().then(() => {
          this.showWidget = false;
        });
      } else {
        this.supportUnavailableTpl?.open().then();
      }
    }));
  }

  protected closeSupportTicket(): void {
    unsubscribe(this.formSubs);
    this.formService.destroyWrapper(this.wrapper);
    this.clearScreenshot();
    this.showWidget = true;
    this.showAttachments = false;
    this.supportTicketTpl?.close();
  }

  protected createTicket(): void {
    this.wrapper?.submit();
  }

  protected isScreenshotSupported(): boolean {
    return isScreenshotSupported();
  }

  protected removeScreenshot(): void {
    this.wrapper?.setValue('screenshot', false);
  }

  // Taken when switched on, not when sent: the user sees the picture and can remove it before it leaves
  private async takeScreenshot(): Promise<void> {
    this.capturing = true;
    document.body.classList.add('screenshotting');
    try {
      const blob: Blob = await captureTab();
      this.clearScreenshot();
      this.screenshot = blob;
      this.screenshotUrl = URL.createObjectURL(blob);
    } catch (ex) {
      this.wrapper?.setValue('screenshot', false);
      // Cancelling the picker is a decision, not an error
      if (!(ex instanceof DOMException && ex.name === 'NotAllowedError')) {
        this.log.error('Screenshot create error: {}', [ex]);
        this.addGlobalError('support.screenshot.create.error', '91000');
      }
    } finally {
      document.body.classList.remove('screenshotting');
      this.capturing = false;
    }
  }

  private clearScreenshot(): void {
    if (isDefined(this.screenshotUrl)) {
      URL.revokeObjectURL(this.screenshotUrl);
    }
    this.screenshot = undefined;
    this.screenshotUrl = undefined;
  }

  private buildForm(): void {
    const subject = this.i18n.translate('support.subject.default');
    this.wrapper = this.formService.buildWrapper('support-widget', new FormOptions({
      autoSubmit: false,
      changeDelayTime: 100,
      fixedFormState: FormStateEnum.EMPTY
    }));
    this.wrapper.build({
      subject: [subject, [Validators.required, Validators.maxLength(250)]],
      description: ['', [Validators.required, Validators.maxLength(AccValidator.TEXT_MAX_LENGTH)]],
      priority: [Priority.LOW, [Validators.required]],
      screenshot: [false],
      attachment: [false],
      attachment1: [],
      attachment2: [],
      attachment3: [],
    }, [
      new FormMetadata('subject').setTranslation('support.subject'),
      new FormMetadata('description').setTranslation('support.description'),
      new FormMetadata('priority').setSelectEnum(Priority, 'priority.', Priority.LOW, [Priority.FALLBACK]),
      new FormMetadata('screenshot').setTranslation('support.screenshot.create'),
      new FormMetadata('attachment').setTranslation('support.attachment.create'),
      new FormMetadata('attachment1').setTranslation('attachment'),
      new FormMetadata('attachment2').setTranslation('attachment'),
      new FormMetadata('attachment3').setTranslation('attachment'),
    ]);
    this.formSubs.push(
      this.wrapper.onChangeByNameDistinctValue('attachment')
        .subscribe((v) => this.showAttachments = isTrue(v.getValue())),
      this.wrapper.onChangeByNameDistinctValue('screenshot').subscribe((v) => {
        if (isTrue(v.getValue())) {
          this.takeScreenshot().then();
        } else {
          this.clearScreenshot();
        }
      }),
      this.wrapper.onSubmit().subscribe((v) => this.processSubmit(v))
    );
  }

  private processSubmit(v: any): void {
    const attachments: File[] = [this.attachment1?.getFile(), this.attachment2?.getFile(), this.attachment3?.getFile()]
      .filter((f) => isDefined(f));
    if (notDefined(this.wrapper) || this.wrapper.isInvalid()) {
      this.wrapper?.markAllInvalidAsTouched();
    } else if (this.capturing) {
      this.log.debug('Support ticket waits for its screenshot');
    } else if (this.isTooLarge(attachments)) {
      this.addGlobalError('validation.file.size', '91001');
    } else {
      this.disableConfirmButton(true);
      this.formSubs.push(this.submitNewTicket(v, attachments).subscribe({
        next: () => {
          this.notificationService.addSuccessMessage('support.ticket.create.success');
          this.disableConfirmButton(false);
          this.closeSupportTicket();
        },
        error: (err) => {
          this.wrapper?.parseError(err);
          this.disableConfirmButton(false);
        },
      }));
    }
  }

  private isTooLarge(attachments: File[]): boolean {
    const files: Blob[] = isDefined(this.screenshot) ? [...attachments, this.screenshot] : attachments;
    const total: number = files.reduce((sum, f) => sum + f.size, 0);
    return files.some((f) => f.size > SupportWidgetComponent.MAX_FILE_BYTES) || total > SupportWidgetComponent.MAX_REQUEST_BYTES;
  }

  // Page without parameters and the app version go into an internal note for the support - nothing about the device
  private submitNewTicket(v: any, attachments: File[]): Observable<SupportTicketDto> {
    const ticket = new SupportTicketDto({
      subject: v.subject,
      description: v.description,
      priority: v.priority,
      page: this.router.url.split(/[;?#]/)[0],
      appVersion: this.version
    });
    return this.supportTicketService.createTicket(ticket, this.screenshot, attachments);
  }

  private addGlobalError(messageCode: string, errorCode: string): void {
    this.wrapper?.addValidationError(new ValidationErrorDto({messageCode, errorCode, field: FormWrapper.VALIDATION_FIELD_GLOBAL}));
  }

  private disableConfirmButton(disabled: boolean): void {
    const button = this.supportTicketTpl?.getInputConfirmButton();
    if (isDefined(button)) {
      button.disabled = disabled;
    }
  }
}
